import React, {useEffect, useMemo, useState} from "react";
import Auth from "../Auth";
import {Navigate, useLocation} from "react-router-dom";
import User from "../models/User";
import {useMount, useUnmount} from "react-use";
import {Helmet} from "react-helmet";
import {useMutation} from "@tanstack/react-query";

let AuthContext = React.createContext(null);

const AuthProvider = (props) => {

    const [uniqId, setUniqId] = useState(TB.uuid());
    const location = useLocation();
    const [auth, setAuth] = useState(() => Auth.user());
    const [isLoaded, setLoaded] = useState(() => Auth.loaded());
    const [isValidated, setIsValidated] = useState(() => Auth.isValidated());
    const [needsValidation, setNeedsValidation] = useState(() => Auth.needsValidation());

    const authRequestMutation = useMutation(['authRequest'], async (callback) => {
        return callback();
    })
    useMount(() => {
        Auth.Intended = location;
    });

    useEffect(() => {
        TB.location = location;

    }, [location.pathname]);

    const shouldHideNavbar = useMemo(() => {
        return Auth.shouldHideNavbar(location)
    }, [location?.pathname, isLoaded, auth]);

    const handleAuth = async(event) => {
        await setAuth(Auth.user())
        await setLoaded(Auth.loaded());
        await setIsValidated(Auth.isValidated());
        await setNeedsValidation(Auth.needsValidation());
    }

    const handleExpired = () => handleAuth("Auth@expired")
    const handleLoad = () => handleAuth("Auth@load")
    const handleLogin = () => handleAuth("Auth@login")
    const handle2FAValidation = () => handleAuth("Auth@2FAValidate")
    const handleLogout = () => handleAuth("Auth@logout")
    const handleUpdate = () => handleAuth("Auth@update")
    useMount(() => {
        TB.event().listen("Auth@expired", handleExpired, uniqId)
        TB.event().listen("Auth@load", handleLoad, uniqId)
        TB.event().listen("Auth@login", handleLogin, uniqId)
        TB.event().listen("Auth@logout", handleLogout, uniqId)
        TB.event().listen("Auth@update", handleUpdate, uniqId)
        TB.event().listen("Auth@2FAValidate", handle2FAValidation, uniqId)
    })
    useUnmount(() => {
        TB.event().provider().off("Auth@expired", handleExpired, uniqId)
        TB.event().provider().off("Auth@load", handleLoad, uniqId)
        TB.event().provider().off("Auth@login", handleLogin, uniqId)
        TB.event().provider().off("Auth@logout", handleLogout, uniqId)
        TB.event().provider().off("Auth@update", handleUpdate, uniqId)
        TB.event().provider().off("Auth@2FAValidate", handle2FAValidation, uniqId)
    })


    return (
        <AuthContext.Provider
            value={{
                auth,
                isLoaded,
                isValidated,
                needsValidation,
                shouldHideNavbar,
                client_org_regions: auth?.client_organization?.regions || [],
                validate2FA: (props) => Auth.validate2FA(props),
                refresh2FA: (props) => Auth.refresh2FA(props),
                isAppropriate: (location) => Auth.isAppropriate(location),
                appropriate: () => Auth.appropriate(),
                refresh: () => Auth.refresh(),
                clearUser: () => Auth.clearUser(),
                user: () => Auth.user(),
                loaded: () => Auth.loaded(),
                exists: () => Auth.exists(),
                expire: () => Auth.expire(),
                update: (props, silent = false) => Auth.update(props, silent = false),
                login: (creds) => Auth.login(creds),
                logout: () => Auth.logout(),

                shouldSeeTalentWelcome: () => Auth.shouldSeeTalentWelcome(),
                shouldSeeTalentAppUpdate: () => Auth.shouldSeeTalentAppUpdate(),
                markTalentAppUpdateAsViewed: () => Auth.markTalentAppUpdateAsViewed(),
                persistConferenceLinkToProfile: link => Auth.persistConferenceLinkToProfile(link),
                markTalentWelcomeAsViewed: () => Auth.markTalentWelcomeAsViewed(),
                registeredClientUser: () => Auth.registeredClientUser(),
                registeredTalentUser: () => Auth.registeredTalentUser(),

                startClientSession: (client) => Auth.startClientSession(client),
                talentSignup: (creds) => Auth.talentSignup(creds),
                talentHasInputResponseError: () => Auth.talentHasInputResponseError(),
                isUpdating: authRequestMutation.isLoading,
                talentUpdate: (params) => {
                    return authRequestMutation.mutateAsync(() => Auth.talentUpdate(params))
                },
                submitAppealForRejectedProfile: params => Auth.submitAppealForRejectedProfile(params),
                deactivateAccount: () => Auth.deactivateAccount(),
                reactivateAccount: () => Auth.reactivateAccount(),
                authSettingsConfig: (auth) => Auth.authSettingsConfig(auth),
                captureAsIntended: () => Auth.captureAsIntended(),
                intended: () => Auth.intended(),
            }}>
            {props.children}
        </AuthContext.Provider>
    )
}


export default AuthProvider;

const AuthMiddleware = ({children}) => {

    const {auth, isLoaded, appropriate, refresh, isValidated,
        needsValidation, isAppropriate} = useAuth();

    // console.log('AuthMiddleware x1 ', {isValidated, needsValidation, isAppropriate, auth})

    const location = useLocation();
    useMount(() => {
        appropriate(location)
        return refresh()
    })
    const shouldRedirect = useMemo(() => {

        if (location?.pathname) {
            switch (location.pathname) {
                default:
                    return isLoaded && !auth
                /*
                |--------------------------------------------------------------------------
                | Public Routes
                |--------------------------------------------------------------------------
                */

                case '/':
                case '/legal/terms':
                case '/legal/privacy-policy':
                case '/legal/cookie-policy':
                    return false;

                /*
                |--------------------------------------------------------------------------
                | Auth/Session Routes
                |--------------------------------------------------------------------------
                */
                case '/settings/notifications/all/opt-out':
                case '/settings/notifications/manage':
                case (location.pathname.match(/^\/settings\/notifications\/email\/(.*)\/opt-out/) || {}).input:
                    return false;

                case '/password/forgot':
                case (location.pathname.match(/^\/password\/reset\/(.*)/) || {}).input:
                    return !!auth

                case '/login':
                case '/signup/talent':
                case '/register':
                case (location.pathname.match(/^\/register\/(.*)/) || {}).input:
                case (location.pathname.match(/^\/signup\/talent\/(.*)/) || {}).input:
                case (location.pathname.match(/^\/signup\/invite\/talent\/(.*)/) || {}).input:
                    console.log("AuthMiddleware -> Should redirect ", {isValidated, needsValidation, auth, is: !!auth || isValidated === false || needsValidation})
                    return !!auth || (isValidated === false && needsValidation)

                case '/authorize':
                    return (auth || isValidated !== false) && needsValidation;
            }
        }
        return false;
    }, [location?.pathname, auth, isLoaded, isValidated, needsValidation]);

    if (shouldRedirect) {
        return <Navigate to={Auth.appropriate()}/>
    }

    return children;
}

const useAuth = () => {
    return React.useContext(AuthContext);
}
const AuthLoadedProvider = (props) => {

    useEffect(() => {
        return () => {
            Auth.User = laravel.auth ? User.make(laravel.auth) : undefined;
        };
    }, []);


    return (
        <AuthContext.Provider
            value={{
                auth: laravel.auth ? User.make(laravel.auth) : undefined,
                isLoaded: true,
                refresh: () => Auth.refreshUser()
            }}>
            <Helmet>
                <title>Wripple</title>
            </Helmet>
            {props.children}
        </AuthContext.Provider>
    )
}
export {
    useAuth,
    AuthContext,
    AuthMiddleware,
    AuthLoadedProvider
}